import { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Button, Col, Colors, InputSearch, Row, Text } from 'basic-ui-lib';
import StoreCard from '../StoreCard';
import { IStore } from 'modules/store/interfaces/api';
import useGetListStores from 'modules/store/services/hooks/useGetListStore';
import useDebounce from 'hooks/useDebounce';

interface SelectStoreProps {
  onContinue: (storeId: string) => void;
}

const SelectStore = ({ onContinue }: SelectStoreProps) => {
  const { storesList: storesData, mutate: fetchStores } = useGetListStores(true);
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const hasFetchedRef = useRef(false);

  const handleSearch = useCallback((searchText: string) => {
    fetchStores({ page: 1, size: 20, keySearch: searchText || undefined });
  }, [fetchStores]);

  const { text: searchValue, setText: setSearchValue } = useDebounce({
    value: '',
    timeDebounce: 400,
    onDebounce: handleSearch,
  });

  useEffect(() => {
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;
    fetchStores({ page: 1, size: 20 });
  }, [fetchStores]);

  const stores = storesData?.content ?? [];

  const handleContinue = () => {
    if (selectedStoreId) {
      onContinue(selectedStoreId);
    }
  };

  return (
    <Container>
      <Col gap={8}>
        <Text caption_2 medium caplock color='neutral.text.5' left>
          SELECT STORES
        </Text>
        <InputSearch
          value={searchValue}
          onChange={setSearchValue}
          placeholder='Search store name/phone'
        />
      </Col>

      <StoreGrid>
        {stores.map((store: IStore) => (
          <StoreCard
            key={store.storeId}
            store={store}
            selected={selectedStoreId === store.storeId}
            onClick={() => setSelectedStoreId(store.storeId)}
          />
        ))}
      </StoreGrid>

      <Row justifyContent='center' paddingTop={16}>
        <Button
          type_second
          lg
          disabled={!selectedStoreId}
          onClick={handleContinue}
          style={{ minWidth: 232 }}
        >
          Continue
        </Button>
      </Row>
    </Container>
  );
};

export default SelectStore;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StoreGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: ${Colors.neutral.bg[2]};
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${Colors.neutral.border[2]};
    border-radius: 2px;
  }
`;
